import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Row {
  id: number;
  name: string;
  description?: string | null;
  parent_id?: number | null;
  slug: string;
  created_at: string;
}

interface Props {
  title: string;
  queryKey: string;
  fetchFn: () => Promise<Row[]>;
  createFn: (data: Partial<Row>) => Promise<Row>;
  updateFn: (id: number, data: Partial<Row>) => Promise<Row>;
  deleteFn: (id: number) => Promise<{ success: boolean }>;
  hasParent?: boolean;
}

export default function SettingsCRUDTable({
  title, queryKey, fetchFn, createFn, updateFn, deleteFn, hasParent = false,
}: Props) {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: [queryKey], queryFn: fetchFn });

  const [form, setForm] = useState({ name: '', description: '', parent_id: '' });
  const [editing, setEditing] = useState<Row | null>(null);

  function resetForm() { setForm({ name: '', description: '', parent_id: '' }); setEditing(null); }

  const create = useMutation({
    mutationFn: () => createFn({
      name: form.name,
      description: form.description || null,
      parent_id: hasParent && form.parent_id ? parseInt(form.parent_id) : null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [queryKey] }); resetForm(); },
  });

  const update = useMutation({
    mutationFn: () => updateFn(editing!.id, {
      name: form.name,
      description: form.description || null,
      parent_id: hasParent && form.parent_id ? parseInt(form.parent_id) : null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [queryKey] }); resetForm(); },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteFn(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
  });

  function startEdit(row: Row) {
    setEditing(row);
    setForm({
      name: row.name,
      description: row.description ?? '',
      parent_id: String(row.parent_id ?? ''),
    });
  }

  const isSaving = create.isPending || update.isPending;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Add / Edit form */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          {editing ? `Edit ${title.replace(/s$/, '')}` : `Add New ${title.replace(/s$/, '')}`}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="label">Name</label>
            <input className="input" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <p className="text-xs text-gray-400 mt-1">The name as it appears in the app.</p>
          </div>

          {hasParent && (
            <div>
              <label className="label">Parent Category</label>
              <select className="input" value={form.parent_id}
                onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}>
                <option value="">None</option>
                {rows.filter((r) => r.id !== editing?.id).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="label">Description</label>
            <textarea className="input resize-none" rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>

          {(create.isError || update.isError) && (
            <p className="text-sm text-red-600">
              {((create.error || update.error) as Error).message}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              className="btn-primary text-xs"
              disabled={!form.name || isSaving}
              onClick={() => editing ? update.mutate() : create.mutate()}
            >
              {isSaving ? 'Saving…' : editing ? `Update ${title.replace(/s$/, '')}` : `Add New ${title.replace(/s$/, '')}`}
            </button>
            {editing && (
              <button className="btn-ghost text-xs" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-sm font-medium text-gray-700">{title}</span>
          <span className="text-xs text-gray-500">{rows.length} items</span>
        </div>
        {isLoading && (
          <div className="p-4 text-sm text-gray-500">Loading…</div>
        )}
        <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 group">
              <div>
                <p className="text-sm font-medium text-gray-900">{row.name}</p>
                {row.description && (
                  <p className="text-xs text-gray-500 truncate max-w-[200px]">{row.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  className="text-xs text-brand-600 hover:text-brand-700"
                  onClick={() => startEdit(row)}
                >
                  Edit
                </button>
                <button
                  className="text-xs text-red-600 hover:text-red-700"
                  onClick={() => {
                    if (confirm(`Delete "${row.name}"?`)) remove.mutate(row.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
